import React from 'react';
import { isValid, parseISO } from 'date-fns';
import { useLocation, useParams } from 'react-router';

import config from 'config/site.config';
import useObjectSearchParams from 'hooks/useObjectSearchParams';
import useSetDeleteURLParams from 'hooks/useSetDeleteURLParams';
import type { SeverityLevel } from 'model/Alerts/types';
import { ProjectsAlertsTabs } from 'model/Alerts/types';

const {
  modules: { alerts: alertsUrls, resourcesAndMachines: resourcesUrls },
} = config;

export interface AlertsFilterParams {
  search?: string;
  projectIds?: string;
  status?: string;
  fromDate?: string;
  toDate?: string;
  assignedToId?: string;
  submittedById?: string;
  machineId?: string;
  needsFollowUp?: 'true';
  risk?: string;
  typeId?: string;
  categoryId?: string;
  relatedToId?: string;
  causeId?: string;
  involvedPartiesId?: string;
}

const useAlertsFilters = () => {
  const { pathname } = useLocation();

  const { resourceId } = useParams<{
    resourceId?: string;
  }>();

  const {
    search,
    projectIds,
    status,
    fromDate,
    toDate,
    assignedToId,
    submittedById,
    machineId,
    needsFollowUp,
    risk,
    typeId,
    categoryId,
    relatedToId,
    causeId,
    involvedPartiesId,
  } = useObjectSearchParams<AlertsFilterParams>();

  const {
    setURLParam,
    setMultipleURLParams,
    deleteURLParam,
    deleteMultipleURLParams,
  } = useSetDeleteURLParams<AlertsFilterParams>();

  const fromDateDate = fromDate && parseISO(fromDate);
  const toDateDate = toDate && parseISO(toDate);

  const alertsAcrossCompany = pathname.includes(alertsUrls.companyAlertsUrl);
  const alertsForCurrentUser = pathname.includes(alertsUrls.userAlertsUrl);

  const alertsForMachines =
    !!resourceId || !!machineId || pathname.includes(resourcesUrls.baseUrl);
  const defaultStatus = alertsForCurrentUser
    ? ProjectsAlertsTabs.InProgress
    : ProjectsAlertsTabs.Open;

  const filters = {
    search: search || '',
    alertsAcrossCompany,
    alertsForCurrentUser,
    alertsForMachines,
    projectIds:
      typeof projectIds === 'string' ? projectIds.split(',') : undefined,
    fromDate: isValid(fromDateDate) ? (fromDateDate as Date) : undefined,
    toDate: isValid(toDateDate) ? (toDateDate as Date) : undefined,
    assignedToId,
    submittedById,
    resourceId,
    machineId,
    needsFollowUp: needsFollowUp === 'true',
    status: !Number.isNaN(Number(status))
      ? (Number(status) as ProjectsAlertsTabs)
      : defaultStatus,
    risk: !Number.isNaN(Number(risk))
      ? (Number(risk) as SeverityLevel)
      : undefined,
    typeId,
    categoryId,
    relatedToId,
    causeId,
    involvedPartiesId,
    setURLParam,
    setMultipleURLParams,
    deleteURLParam,
    deleteMultipleURLParams,
  };

  let filtersCount = 0;
  [
    filters.assignedToId,
    filters.submittedById,
    filters.machineId,
    filters.needsFollowUp,
    filters.typeId,
    filters.categoryId,
    filters.relatedToId,
    filters.causeId,
    filters.involvedPartiesId,
  ].forEach((filter) => {
    if (filter) {
      filtersCount += 1;
    }
  });
  if (typeof risk === 'number') {
    filtersCount += 1;
  }

  const result = React.useMemo(
    () => ({ filtersCount, ...filters }),
    [filters, filtersCount]
  );

  return result;
};

export default useAlertsFilters;
